import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchCampaign as fetchCampaignAPI, fetchCampaigns, fetchCampaignsCount as fetchCampaignsCountAPI, markCampaignComplete, requestCampaignApproval } from "services/api/campaign";
import { ERROR } from "types";
import { CampaignDetail, CampaignsFilters, CampaignsResponse, CampaignsSlice } from "types/campaign";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

const getTotalsFilterKey = (agency_id?: string, country?: string) =>
    `${agency_id || 'all'}-${country || 'all'}`;

const buildListParams = (filters: CampaignsFilters) => {
    const { page, status, q, agency_id, country } = filters;
    return {
        page,
        status,
        ...(q ? { q } : {}),
        ...(agency_id ? { agency_id } : {}),
        ...(country ? { country } : {}),
    };
};

export const fetchCampaignsList = createAsyncThunk<
    { campaigns: CampaignsResponse, filters: CampaignsFilters, filterId: string },
    { filters: CampaignsFilters, force?: boolean },
    { rejectValue: string }
>(
    'campaigns/list',
    async ({ filters, force = false }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { campaigns: CampaignsSlice };
            const filterId = generateUniqueKey(filters, ['page']);
            const STALE_TIME = apiConfig.FETCH_CAMPAIGNS.staleTime;

            if (filters.page === 1 && !force && isFresh(state.campaigns.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const campaigns = await fetchCampaigns(buildListParams(filters));
            return { campaigns, filters, filterId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaigns list!"));
        }
    }
);

export const fetchCampaignsCount = createAsyncThunk<
    { totals: Record<string, number>, filterKey: string },
    { agency_id?: string, country?: string, force?: boolean },
    { rejectValue: string }
>(
    'campaigns/total',
    async ({ agency_id, country, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGNS_COUNT.staleTime;
            const state = getState() as { campaigns: CampaignsSlice };
            const filterKey = getTotalsFilterKey(agency_id, country);

            if (!force && isFresh(state.campaigns.totals[filterKey]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const totals = await fetchCampaignsCountAPI({ agency_id, country });
            return { totals, filterKey };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaign counts"));
        }
    }
);

export const fetchCampaign = createAsyncThunk<
    { campaignId: string, campaignData: CampaignDetail },
    { campaignId: string, force?: boolean },
    { rejectValue: string }
>(
    'campaigns/campaign',
    async ({ campaignId, force = false }, { getState, rejectWithValue }) => {
        const state = getState() as { campaigns: CampaignsSlice };
        const STALE_TIME = apiConfig.FETCH_CAMPAIGN.staleTime;

        if (!force && isFresh(state.campaigns.items?.[campaignId]?.last_fetched_at ?? null, STALE_TIME) && !state.campaigns.items?.[campaignId]?.expired) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const campaignData = await fetchCampaignAPI(campaignId);
            return { campaignId, campaignData };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch campaign with ID: " + campaignId));
        }
    }
);

export const markCampaignCompleted = createAsyncThunk<
    { campaignId: string },
    { campaignId: string },
    { rejectValue: string }
>(
    'campaign/mark-completed',
    async ({ campaignId }, { rejectWithValue }) => {
        try {
            await markCampaignComplete(campaignId);
            return { campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to mark campaign as completed!"));
        }
    }
);

export const sendCampaignForApprovalRequest = createAsyncThunk<
    { campaignId: string },
    { campaignId: string },
    { rejectValue: string }
>(
    'campaign/send-for-approval',
    async ({ campaignId }, { rejectWithValue }) => {
        try {
            await requestCampaignApproval(campaignId);
            return { campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to request approval!"));
        }
    }
);
